import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { linkOptions, type LinkOptions } from "@tanstack/react-router";

import Icon from "@/utils/icons";

export enum NavigationItemType {
  NavigationGroup = "navigationGroup",
  NavigationItem = "navigationItem",
}
export interface NavigationItem {
  type: NavigationItemType.NavigationItem;
  title: string;
  icon: ReactNode;
  to: LinkOptions;
}

export interface NavigationGroup {
  type: NavigationItemType.NavigationGroup;
  title: string;
  icon: ReactNode;
  items: NavigationItem[];
}

export type NavigationCollection = (NavigationItem | NavigationGroup)[];

export function isNavigationGroup(item: NavigationItem | NavigationGroup): item is NavigationGroup {
  return item.type === NavigationItemType.NavigationGroup;
}

export function useNavigationElementsWorkspace(
  workspace_id?: string,
  canManageChildWorkspaces?: boolean,
): NavigationCollection {
  const { t } = useTranslation();
  if (!workspace_id) return [];
  return [
    {
      type: NavigationItemType.NavigationItem,
      icon: <Icon.DAILY_SUMMARY />,
      title: t("navigation.daily-summary"),
      to: linkOptions({
        to: "/workspaces/$workspace_id",
        params: {
          workspace_id,
        },
        activeOptions: { exact: true },
      }),
    },
    {
      type: NavigationItemType.NavigationItem,
      title: t("navigation.workspace-overview"),
      to: linkOptions({
        to: "/workspaces/$workspace_id/overview",
        params: {
          workspace_id,
        },
      }),
      icon: <Icon.WORKSPACE />,
    },
    ...(canManageChildWorkspaces
      ? ([
          {
            type: NavigationItemType.NavigationItem,
            title: t("navigation.customer-workspaces"),
            icon: <Icon.WORKSPACES />,
            to: linkOptions({
              to: "/workspaces/$workspace_id/customer-workspaces",
              params: {
                workspace_id,
              },
            }),
          },
        ] as NavigationCollection)
      : []),
    {
      type: NavigationItemType.NavigationItem,
      title: t("navigation.deployments"),
      icon: <Icon.DEPLOYMENT />,
      to: linkOptions({
        to: "/workspaces/$workspace_id/deployments",
        params: {
          workspace_id,
        },
      }),
    },
    {
      type: NavigationItemType.NavigationItem,
      title: t("navigation.checklist-templates"),
      icon: <Icon.CHECKLIST_TEMPLATE />,
      to: linkOptions({
        to: "/workspaces/$workspace_id/checklist-templates",
        params: {
          workspace_id,
        },
      }),
    },
    {
      type: NavigationItemType.NavigationGroup,
      title: t("navigation.workspace-settings"),
      icon: <Icon.SETTINGS />,
      items: [
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.workspace-details"),
          icon: <Icon.WORKSPACE_DETAILS />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/settings/details",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.workspace-profile"),
          icon: <Icon.WORKSPACE_PROFILE />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/settings/profile",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.users-and-permissions"),
          icon: <Icon.USERS />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/settings/access",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.workspace-tags"),
          icon: <Icon.TAG />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/settings/tags",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.custom-inventory"),
          icon: <Icon.MANUFACTURER />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/settings/custom-inventory",
            params: {
              workspace_id,
            },
          }),
        },
      ],
    },
  ];
}

export function useNavigationElementsDeployment(workspace_id?: string): NavigationCollection {
  const { t } = useTranslation();
  if (!workspace_id) return [];
  return [
    {
      type: NavigationItemType.NavigationItem,
      title: t("navigation.cases"),
      icon: <Icon.CASE />,
      to: linkOptions({
        to: "/workspaces/$workspace_id/cases",
        params: {
          workspace_id,
        },
      }),
    },
    {
      type: NavigationItemType.NavigationItem,
      title: t("navigation.maintenances"),
      icon: <Icon.MAINTENANCE />,
      to: linkOptions({
        to: "/workspaces/$workspace_id/maintenances",
        params: {
          workspace_id,
        },
      }),
    },
    {
      type: NavigationItemType.NavigationItem,
      title: t("navigation.device-insights"),
      icon: <Icon.INSIGHTS />,
      to: linkOptions({
        to: "/workspaces/$workspace_id/device-insights",
        params: {
          workspace_id,
        },
      }),
    },
    {
      type: NavigationItemType.NavigationItem,
      title: t("navigation.maintenance-strategies"),
      icon: <Icon.MAINTENANCE_STRATEGY />,
      to: linkOptions({
        to: "/workspaces/$workspace_id/maintenance-strategies",
        params: {
          workspace_id,
        },
      }),
    },
    {
      type: NavigationItemType.NavigationItem,
      title: t("navigation.devices"),
      icon: <Icon.DEVICE />,
      to: linkOptions({
        to: "/workspaces/$workspace_id/devices",
        params: {
          workspace_id,
        },
      }),
    },
    {
      type: NavigationItemType.NavigationGroup,
      title: t("navigation.spare-parts"),
      icon: <Icon.PART />,
      items: [
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.inventory"),
          icon: <Icon.INVENTORY />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/inventory/parts",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.assembled-parts"),
          icon: <Icon.PART_ASSEMBLY />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/inventory/assembled",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.inventory-activities"),
          icon: <Icon.INVENTORY_ACTIVITIES />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/inventory/activities",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.inventory-constraints"),
          icon: <Icon.CONSTRAINTS />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/inventory/constraints",
            params: {
              workspace_id,
            },
          }),
        },
      ],
    },
  ];
}
